import { useTranslation } from 'react-i18next';

import { RateSourceDTO } from '@/modules/common/types/dto/rate-source.dto';

import { ProjectChangedFieldNameDTO } from '../../types/dto/project-changed-field.dto';
import { ProjectChangedField } from '../../types/project-changes';
import { getLocalizedFieldName } from '../../utils/project-changes/project-change-field-name-mapper';
import {
  formatFieldValue,
  formatValueWithCurrency,
} from '../../utils/project-changes/project-change-field-value-formatter';

interface GroupedFieldItemProps {
  field: ProjectChangedField;
  allFields: ProjectChangedField[];
}

/**
 * Renders grouped field changes (e.g., multiple USER_RATES entries)
 */
export const GroupedFieldItem = ({ field, allFields }: GroupedFieldItemProps) => {
  const { t } = useTranslation('authenticated.projects');

  // Get currency from DEFAULT_CURRENCY_CODE field if it exists
  const currencyField = allFields.find(
    f => f.field === ProjectChangedFieldNameDTO.DEFAULT_CURRENCY_CODE
  );
  const currency = currencyField?.to || currencyField?.from || null;

  const fieldName = getLocalizedFieldName(field.field as ProjectChangedFieldNameDTO);
  const count = field.groupCount || 0;

  return (
    <div className="w-full">
      <div className="flex w-full flex-col">
        <p className="text-sm font-medium leading-5">
          {fieldName}{' '}
          <span className="text-muted-foreground">
            {t('details.tabs.changes.changesCount', { count })}
          </span>
          :
        </p>
        <div className="relative mt-2 flex flex-col gap-3 border-l-2 border-border pl-4">
          {field.subfieldChanges?.map((userChange, index) => {
            const userName = userChange.fieldLocalizedName || t('details.tabs.changes.unknown');

            // Find rate source change
            const rateSourceChange = userChange.subfieldChanges?.find(
              sf => sf.field === ProjectChangedFieldNameDTO.USER_RATE_SOURCE
            );

            // Find custom rate change
            const customRateChange = userChange.subfieldChanges?.find(
              sf => sf.field === ProjectChangedFieldNameDTO.USER_CUSTOM_RATE
            );

            const rateSourceFrom = rateSourceChange?.from
              ? formatFieldValue(rateSourceChange.from, t, rateSourceChange.field)
              : null;
            const rateSourceTo = rateSourceChange?.to
              ? formatFieldValue(rateSourceChange.to, t, rateSourceChange.field)
              : null;
            const rateSourceToRaw = rateSourceChange?.to;
            const customRateFrom = customRateChange?.from;
            const customRateTo = customRateChange?.to;

            // Show custom rate when:
            // 1. Rate source is explicitly set to CUSTOM, or
            // 2. There's no rate source change but the custom rate itself changed (e.g. direct rate edit)
            const hasRateSourceChange = !!rateSourceChange;
            const isCustomRateSource = rateSourceToRaw === RateSourceDTO.CUSTOM;
            const shouldShowCustomRate =
              (customRateTo || customRateFrom) && (isCustomRateSource || !hasRateSourceChange);

            return (
              <div key={`${userName}-${index}`} className="flex flex-col gap-1">
                <p className="text-sm font-medium leading-5">{userName}:</p>
                <ul className="ml-5 list-disc space-y-1">
                  {(rateSourceFrom || rateSourceTo) && (
                    <li className="text-sm leading-5">
                      <span className="font-medium">{t('details.tabs.changes.rateSource')}:</span>
                      {!rateSourceFrom && rateSourceTo ? (
                        <span> {rateSourceTo}</span>
                      ) : (
                        <>
                          {rateSourceFrom && (
                            <span className="text-muted-foreground line-through">
                              {' '}
                              {rateSourceFrom}
                            </span>
                          )}
                          {rateSourceTo && (
                            <span>
                              {' '}
                              {t('details.tabs.changes.valueDisplay.arrow')} {rateSourceTo}
                            </span>
                          )}
                        </>
                      )}
                    </li>
                  )}
                  {shouldShowCustomRate && (
                    <li className="text-sm leading-5">
                      <span className="font-medium">{t('details.tabs.changes.rate')}:</span>
                      {!customRateFrom && customRateTo ? (
                        <span> {formatValueWithCurrency(customRateTo, currency)}</span>
                      ) : (
                        <>
                          {customRateFrom && (
                            <span className="text-muted-foreground line-through">
                              {' '}
                              {formatValueWithCurrency(customRateFrom, currency)}
                            </span>
                          )}
                          {customRateTo && (
                            <span>
                              {' '}
                              {t('details.tabs.changes.valueDisplay.arrow')}{' '}
                              {formatValueWithCurrency(customRateTo, currency)}
                            </span>
                          )}
                        </>
                      )}
                    </li>
                  )}
                </ul>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
